import React from 'react'
import { Container, Header, Message, Icon } from 'semantic-ui-react'

import { useTranslation } from 'react-i18next'
import RevealContactInfo from '../../../common-components/RevealContactInfo'
import ContactCard from '../../../common-components/ContactCard'

import { CUSTOMER_REPS_CUSTOMER_EXPERIENCE, CUSTOMER_REPS_SALES } from '../../../constants'
import {
  WARNING_TEXT1,
  WARNING_TEXT10,
  WARNING_TEXT11,
  WARNING_TEXT12,
  WARNING_TEXT13,
  WARNING_TEXT2,
  WARNING_TEXT3,
  WARNING_TEXT4,
  WARNING_TEXT5,
  WARNING_TEXT5_UKE,
  WARNING_TEXT6,
  WARNING_TEXT7,
  WARNING_TEXT8,
  WARNING_TEXT9,
  WARNING_UKE_HOLIDAY_ORDER_1,
  WARNING_UKE_HOLIDAY_ORDER_2,
  WARNING_UKE_HOLIDAY_ORDER_3,
  WARNING_UKE_HOLIDAY_ORDER_4,
} from '../../../constants/labelCaptions'

// UKE holiday order message
const tz = 'Europe/London'
const now = new Date(new Date().toLocaleString('en-US', { timeZone: tz }))
const start = new Date('2025-11-25T00:00:00+10:00')
const end = new Date('2026-01-06T00:00:00+10:00')
const ukeHolidayConfig = {
  orderByDeadline: 'Monday, 8 December',
  fulfillmentPauseDateRange: '9–24 December',
  fulfillmentResumeDate: 'Monday, 5 January',
}

export default ({
  isCustomerOnCreditHold,
  isCustomerOnReportingHold,
  isCustomerOnSalesHold,
  isCustomerStatusInactive,
  customerName,
  customerId,
  canCustomerNormallyOrderKegs,
  canCustomerNormallyOrderKegCollars,
  isCustomerOnAcknowledgmentHold,
  isCustomerOnAcknowledgmentHoldWarning,
  isCustomerOnReportingHoldWarning,
  isUKECustomer,
}) => {
  if (
    !isCustomerOnAcknowledgmentHold &&
    !isCustomerOnCreditHold &&
    !isCustomerOnReportingHold &&
    !isCustomerStatusInactive &&
    !isCustomerOnSalesHold &&
    !isCustomerOnAcknowledgmentHoldWarning &&
    !isCustomerOnReportingHoldWarning &&
    !(isUKECustomer && now >= start && now <= end)
  ) {
    return null
  }

  const { t: translate } = useTranslation('common')
  let prohibitedFeaturesText
  if (canCustomerNormallyOrderKegs && canCustomerNormallyOrderKegCollars) {
    prohibitedFeaturesText = 'order kegs or keg collars'
  } else if (canCustomerNormallyOrderKegs) {
    prohibitedFeaturesText = 'order kegs'
  } else {
    prohibitedFeaturesText = 'order keg collars'
  }

  const prohibitedFeaturesTextForCreditHold =
    canCustomerNormallyOrderKegs || canCustomerNormallyOrderKegCollars
      ? `${translate(WARNING_TEXT1)} ${translate(prohibitedFeaturesText)} (${translate(WARNING_TEXT2)}).`
      : '' // Master customers can neither order kegs nor order keg collars

  const prohibitedFeaturesTextForInactiveAccount =
    canCustomerNormallyOrderKegs || canCustomerNormallyOrderKegCollars
      ? `, ${translate(WARNING_TEXT3)} ${translate(prohibitedFeaturesText)} (${translate(WARNING_TEXT2)})`
      : '' // Master customers can neither order kegs nor order keg collars

  let headerText
  let messageText
  const messageType = {}
  if (isCustomerOnCreditHold) {
    headerText = 'Credit Hold'
    messageText = `${customerName} ${translate(WARNING_TEXT4)} ${prohibitedFeaturesTextForCreditHold}`
  } else if (isCustomerOnReportingHold) {
    headerText = 'Reporting Hold'
    messageText = isUKECustomer ? translate(WARNING_TEXT5_UKE) : translate(WARNING_TEXT5)
  } else if (isCustomerOnSalesHold) {
    headerText = 'Sales Hold'
    messageText = `${customerName} ${translate(WARNING_TEXT6)} ${prohibitedFeaturesTextForCreditHold}`
  } else if (isCustomerStatusInactive) {
    headerText = 'Inactive Account'
    messageText = `${customerName}'s ${translate(WARNING_TEXT8)}${prohibitedFeaturesTextForInactiveAccount}. ${translate(WARNING_TEXT7)}`
  } else if (isCustomerOnAcknowledgmentHold) {
    headerText = 'Acknowledgment Hold'
    messageText = translate(WARNING_TEXT9)
  } else if (isCustomerOnAcknowledgmentHoldWarning) {
    messageType.warning = true
    headerText = 'Acknowledgment Hold Warning'
    messageText = translate(WARNING_TEXT10)
  } else if (isCustomerOnReportingHoldWarning) {
    messageType.warning = true
    headerText = 'Reporting Hold Warning'
    messageText = `${translate(WARNING_TEXT11)} ${customerName} ${WARNING_TEXT12} ${isCustomerOnReportingHoldWarning} ${translate(WARNING_TEXT13)}`
  }

  const ukeHolidayFulfillmentPauseMessage = ({
    orderByDeadline,
    fulfillmentPauseDateRange,
    fulfillmentResumeDate,
  }) => (
    <Message icon info>
      <Icon name="exclamation circle" />
      <Message.Content>
        <Message.Header style={{ fontSize: '24px' }}>{translate('important')}</Message.Header>
        <Message.List>
          <Message.Item>
            {`${translate(WARNING_UKE_HOLIDAY_ORDER_1)}`} <strong>{`${orderByDeadline}`}</strong>.
          </Message.Item>
          <Message.Item>
            {`${translate(WARNING_UKE_HOLIDAY_ORDER_2)}`}{' '}
            <strong>{fulfillmentPauseDateRange}</strong>.
          </Message.Item>
          <Message.Item>
            {`${translate(WARNING_UKE_HOLIDAY_ORDER_3)}`} <strong>{fulfillmentResumeDate}</strong>{' '}
            {`(${translate(WARNING_UKE_HOLIDAY_ORDER_4)}).`}
          </Message.Item>
        </Message.List>
      </Message.Content>
    </Message>
  )

  return (
    <>
      {isUKECustomer &&
        now >= start &&
        now <= end &&
        ukeHolidayFulfillmentPauseMessage(ukeHolidayConfig)}
      {(isCustomerOnAcknowledgmentHold ||
        isCustomerOnCreditHold ||
        isCustomerOnReportingHold ||
        isCustomerStatusInactive ||
        isCustomerOnSalesHold ||
        isCustomerOnAcknowledgmentHoldWarning ||
        isCustomerOnReportingHoldWarning) && (
        <Container>
          <Message negative {...messageType}>
            <Header as="h2">{`${translate(headerText)}: ${customerName}`}</Header>
            <p>{messageText}</p>
            {(isCustomerOnCreditHold || isCustomerOnReportingHold || isCustomerOnSalesHold) && (
              <ContactCard
                customerId={customerId}
                repType={
                  isCustomerOnSalesHold ? CUSTOMER_REPS_SALES : CUSTOMER_REPS_CUSTOMER_EXPERIENCE
                }
              />
            )}
            {isCustomerStatusInactive && (
              <RevealContactInfo
                customerId={customerId}
                repType={CUSTOMER_REPS_CUSTOMER_EXPERIENCE}
              />
            )}
          </Message>
        </Container>
      )}
    </>
  )
}
